import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Flame, X } from 'lucide-react';

export const PromoBanner = ({ promoEndDate, offersCount }) => {
    const [timeLeft, setTimeLeft] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        if (!promoEndDate) return;
        const end = new Date(promoEndDate).getTime();

        const tick = () => {
            const now = Date.now();
            const diff = end - now;
            if (diff <= 0) {
                setTimeLeft(null);
                return;
            }
            setTimeLeft({
                days: Math.floor(diff / (1000 * 60 * 60 * 24)),
                hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
                minutes: Math.floor((diff / (1000 * 60)) % 60),
                seconds: Math.floor((diff / 1000) % 60),
            });
        };

        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [promoEndDate]);

    if (!timeLeft || dismissed) return null;

    return (
        <div
            className="relative overflow-hidden bg-gradient-to-r from-red-600 via-red-500 to-orange-500"
            data-testid="promo-banner"
        >
            {/* Animated shimmer */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div
                    className="absolute -inset-full h-full w-1/3 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                    style={{ animation: 'shimmer 3s ease-in-out infinite' }}
                />
            </div>

            <style>{`
                @keyframes shimmer {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(400%); }
                }
                @keyframes pulse-glow {
                    0%, 100% { box-shadow: 0 0 8px rgba(255,255,255,0.3); }
                    50% { box-shadow: 0 0 18px rgba(255,255,255,0.6); }
                }
            `}</style>

            <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-center gap-3 sm:gap-6 relative z-10">
                {/* Fire icon */}
                <Flame className="h-5 w-5 text-yellow-300 flex-shrink-0 animate-pulse" />

                {/* Message */}
                <div className="flex items-center gap-2 sm:gap-4 flex-wrap justify-center">
                    <span className="text-white font-bold text-xs sm:text-sm tracking-wide uppercase">
                        {offersCount} Offres de la Semaine
                    </span>

                    {/* Countdown */}
                    <div className="flex items-center gap-1" data-testid="promo-countdown">
                        <Clock className="h-3.5 w-3.5 text-yellow-200 mr-1" />
                        {[
                            { val: timeLeft.days, label: 'j' },
                            { val: timeLeft.hours, label: 'h' },
                            { val: timeLeft.minutes, label: 'm' },
                            { val: timeLeft.seconds, label: 's' },
                        ].map((u) => (
                            <span key={u.label} className="inline-flex items-center">
                                <span
                                    className="bg-white/20 backdrop-blur-sm text-white font-mono font-bold text-xs px-1.5 py-0.5 rounded"
                                    style={{ animation: 'pulse-glow 2s ease-in-out infinite', minWidth: '28px', textAlign: 'center' }}
                                >
                                    {String(u.val).padStart(2, '0')}
                                </span>
                                <span className="text-yellow-200 text-[10px] font-medium ml-0.5 mr-1">{u.label}</span>
                            </span>
                        ))}
                    </div>
                </div>

                {/* CTA */}
                <Link
                    to="/catalogue?tab=offers"
                    className="flex items-center gap-1 bg-white text-red-600 font-bold text-xs px-3 py-1.5 rounded-full hover:bg-yellow-100 transition-colors flex-shrink-0"
                    data-testid="promo-banner-cta"
                >
                    Voir les offres
                    <ArrowRight className="h-3 w-3" />
                </Link>

                {/* Close */}
                <button
                    onClick={() => setDismissed(true)}
                    className="text-white/70 hover:text-white transition-colors flex-shrink-0"
                    data-testid="promo-banner-close"
                    aria-label="Fermer"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
};
